import {
  Appliance,
  ApplianceConfiguration,
  ApplianceFilter,
  ApplianceInputInfo,
  ApplianceOutputInfo,
  AppliancePhysicalPortInfo,
  ApplianceRoutingRole,
  ApplianceVariant,
  ApplianceType,
  BundleVersions,
  DrainApplianceResult,
  GeoApplianceResult,
  Group,
  ListApplianceSortableField,
  ListResult,
  NdiSource,
  Pcap,
  RistserverLogKind,
  RistserverLogSnapshot,
  RistserverLogSnapshotWithPayload,
  SortOrder,
  UpdateAppliancePayload,
} from 'common/api/v1/types'
import {
  AppliancesRequestParams,
  EnrichedAppliance,
  EnrichedApplianceWithOwner,
  PaginatedRequestParams,
  singleSortQueryFromPaginatedRequestParams,
} from '../nm-types'
import { parseFilterValues } from '../filterValues'
import { EdgeClient } from 'common/generated/edgeClient'
import { Query } from 'common/query'
import type { RequestOptions } from 'common/network/request-options'

export class AppliancesApi {
  constructor(private readonly edgeClient: EdgeClient) {}

  listAppliancePcaps(id: string): Promise<ListResult<Pcap>> {
    return this.edgeClient.listAppliancePcaps(id)
  }

  listApplianceNdiSources(id: string): Promise<ListResult<NdiSource>> {
    return this.edgeClient.listApplianceNdiSources(id)
  }

  recreateTunnels(id: string): Promise<void> {
    return this.edgeClient.recreateApplianceTunnels(id)
  }

  deletePcap(applianceId: string, pcapId: string): Promise<Pcap[]> {
    return this.edgeClient.deleteAppliancePcap(applianceId, pcapId) as any
  }

  collectApplianceRistserverLog(id: string, kind: RistserverLogKind): Promise<RistserverLogSnapshot> {
    return this.edgeClient.collectApplianceRistserverLog(id, { kind })
  }

  listApplianceRistserverLogs(id: string): Promise<ListResult<RistserverLogSnapshot>> {
    return this.edgeClient.listApplianceRistserverLogs(id)
  }

  getApplianceRistserverLog(applianceId: string, logId: string): Promise<RistserverLogSnapshotWithPayload> {
    return this.edgeClient.getApplianceRistserverLog(applianceId, logId)
  }

  deleteApplianceRistserverLog(applianceId: string, logId: string): Promise<unknown> {
    return this.edgeClient.deleteApplianceRistserverLog(applianceId, logId)
  }

  removeAppliance(id: string): Promise<Pick<Appliance, 'id'>> {
    return this.edgeClient.deleteAppliance(id)
  }
  updateAppliance(id: Appliance['id'], values: UpdateAppliancePayload): Promise<Appliance> {
    return this.edgeClient.updateAppliance(id, values)
  }

  /**
   * Getting appliance populating it with full owner object and physical ports' owners as well
   * @param id - appliance Id
   */
  async getAppliance(id: string): Promise<EnrichedAppliance> {
    const appliance = await this.edgeClient.getAppliance(id)
    const groupIds = Array.from(new Set([appliance.owner, ...appliance.physicalPorts.map((p) => p.owner)]))
    const groups = (await this.edgeClient.listGroups({ filter: { ids: groupIds } })).items
    const enrichedAppliance = {
      ...appliance,
      _owner: groups.find(({ id }) => id === appliance.owner)!,
      _physicalPorts: appliance.physicalPorts.map((port) => ({
        ...port,
        _owner: groups.find(({ id }) => id === port.owner)!,
      })),
    }
    return enrichedAppliance
  }

  /**
   * Getting ListResult of appliances populating each item with owner group object
   * @param requestParams
   */
  async getAppliances(requestParams: AppliancesRequestParams): Promise<ListResult<EnrichedApplianceWithOwner>> {
    const applianceList = await this.getBareAppliances(requestParams)
    if (applianceList.items.length === 0) {
      return { ...applianceList, items: [] }
    }

    const groupIds = applianceList.items.reduce<Array<string>>((acc, a) => {
      if (acc.includes(a.owner)) return acc
      return acc.concat(a.owner)
    }, [])
    const groups = (await this.edgeClient.listGroups({ filter: { ids: groupIds } })).items
    const groupsMap = groups.reduce<{ [key: string]: Group }>((acc, item) => ({ ...acc, [item.id]: item }), {})

    return {
      ...applianceList,
      items: applianceList.items.map((a) => ({
        ...a,
        _owner: groupsMap[a.owner],
      })),
    }
  }

  getBareAppliances(
    {
      owner: groupId,
      groupName,
      filter: searchName,
      types,
      roles,
      variants,
      regions,
      tags,
      ...params
    }: AppliancesRequestParams,
    options?: Pick<RequestOptions, 'signal'>,
  ): Promise<ListResult<Appliance>> {
    const filter: ApplianceFilter = {
      group: groupId,
      groupName,
      searchName,
      regionNames: regions ? parseFilterValues(regions) : undefined,
      // `types` is a programmatic enum param (plain comma-joined by the route builder, values never
      // contain , or %), so it isn't serializeFilterValues-encoded like the user-facing filters here.
      types: types?.split(',') as ApplianceType[] | undefined,
      roles: roles ? (parseFilterValues(roles) as ApplianceRoutingRole[]) : undefined,
      variants: variants ? (parseFilterValues(variants) as ApplianceVariant[]) : undefined,
      tags: tags ? parseFilterValues(tags) : undefined,
    }
    const query = singleSortQueryFromPaginatedRequestParams({ filter, paginatedRequestParams: params })
    return this.listAppliances(query, options)
  }

  listAppliances(
    query?: Query<ApplianceFilter, SortOrder<ListApplianceSortableField>>,
    options?: Pick<RequestOptions, 'signal'>,
  ): Promise<ListResult<Appliance>> {
    return this.edgeClient.listAppliances(query, options)
  }

  listApplianceRegisteredVariants(): Promise<ListResult<ApplianceVariant>> {
    return this.edgeClient.listApplianceRegisteredVariants()
  }

  listApplianceRegisteredRoles(): Promise<ListResult<ApplianceRoutingRole>> {
    return this.edgeClient.listApplianceRegisteredRoles()
  }

  /**
   * Returns appliances with coordination to show on map
   * @param paginatedRequestParams
   */
  getGeoAppliances(paginatedRequestParams: PaginatedRequestParams): Promise<GeoApplianceResult> {
    const query = singleSortQueryFromPaginatedRequestParams({ filter: undefined, paginatedRequestParams })
    return this.edgeClient.getGeoAppliance(query)
  }

  /**
   * Command to restart appliance
   * @param id
   */
  restart(id: string, requestedVersion?: string): Promise<Appliance> {
    return this.edgeClient.restartAppliance(id, { requestedVersion }, { timeoutMs: 180_000 })
  }

  /**
   * Command to upgrade image for appliance
   * @param id
   */
  upgradeImage(id: string, imageVersion: string): Promise<void> {
    return this.edgeClient.upgradeImageAppliance(id, { imageVersion })
  }

  /**
   * Returns appliance configuration for super user
   * @param id
   */
  getConfig(id: string): Promise<ApplianceConfiguration> {
    return this.edgeClient.getApplianceConfig(id, '')
  }

  listApplianceInputs(id: Appliance['id'], params: PaginatedRequestParams): Promise<ListResult<ApplianceInputInfo>> {
    const query = singleSortQueryFromPaginatedRequestParams({
      filter: undefined,
      paginatedRequestParams: params,
    })
    return this.edgeClient.listApplianceInputs(id, query)
  }

  listApplianceOutputs(id: Appliance['id'], params: PaginatedRequestParams): Promise<ListResult<ApplianceOutputInfo>> {
    const query = singleSortQueryFromPaginatedRequestParams({
      filter: undefined,
      paginatedRequestParams: params,
    })
    return this.edgeClient.listApplianceOutputs(id, query)
  }

  async listAppliancePhysicalPorts(id: Appliance['id']): Promise<AppliancePhysicalPortInfo> {
    return this.edgeClient.getAppliancePorts(id)
  }

  clearCachedApplianceConfig(id: Appliance['id']) {
    return this.edgeClient.clearApplianceConfig(id)
  }

  listApplianceCommands(id: Appliance['id']) {
    return this.edgeClient.listApplianceCommands(id)
  }

  getApplianceSoftwareVersions(): Promise<BundleVersions> {
    return this.edgeClient.getApplianceSoftwareVersions()
  }

  /**
   * Drain appliance by rerouting all upstream inputs away from it
   * @param id - appliance ID
   */
  async drainAppliance(id: Appliance['id']): Promise<DrainApplianceResult> {
    const result = await this.edgeClient._post<DrainApplianceResult>({
      resource: `/api/appliance/${id}/drain`,
    })
    return result.body
  }

  async migrateSerial(id: Appliance['id'], serial: string): Promise<void> {
    await this.edgeClient.migrateSerialAppliance(id, { serial })
  }

  async migrateToEdgeOs(id: Appliance['id']): Promise<void> {
    await this.edgeClient.migrateEdgeOsAppliance(id)
  }
}
